import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { categories } from '@/mocks/dramas';

const navItems = [
  { label: '首页', path: '/' },
  { label: '剧集分类', path: '/category/guochan' },
  { label: '搜索', path: '/search' },
  { label: '关于我们', path: '/about' },
];

export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [query, setQuery] = useState('');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const mobileSearchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setMobileOpen(false);
    setCatOpen(false);
  }, [location.pathname]);

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = query.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : '/search');
  };

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path.split('/').slice(0, 2).join('/'));
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b border-background-200/70 bg-background-50/95 backdrop-blur">
      <div className="mx-auto flex h-16 max-w-content items-center gap-3 px-4 md:px-6">
        <Link to="/" className="flex items-center gap-2 shrink-0">
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500 text-background-50">
            <i className="ri-live-line text-xl" />
          </span>
          <span className="font-heading text-lg font-bold tracking-tight text-foreground-950 whitespace-nowrap">
            最新电视剧
          </span>
        </Link>

        <nav className="ml-4 hidden items-center gap-1 lg:flex">
          <Link
            to="/"
            className={`whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors ${
              isActive('/') ? 'text-primary-600' : 'text-foreground-700 hover:text-primary-600'
            }`}
          >
            首页
          </Link>
          <div className="relative" onMouseEnter={() => setCatOpen(true)} onMouseLeave={() => setCatOpen(false)}>
            <Link
              to="/category/guochan"
              className={`flex items-center gap-1 whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                location.pathname.startsWith('/category')
                  ? 'text-primary-600'
                  : 'text-foreground-700 hover:text-primary-600'
              }`}
            >
              剧集分类
              <i className="ri-arrow-down-s-line text-base" />
            </Link>
            {catOpen && (
              <div className="absolute left-0 top-full w-44 animate-fade-in rounded-lg border border-background-200 bg-background-50 p-2 shadow-sm">
                {categories.map((c) => (
                  <Link
                    key={c.slug}
                    to={`/category/${c.slug}`}
                    className="block rounded-md px-3 py-2 text-sm text-foreground-700 transition-colors hover:bg-primary-50 hover:text-primary-600"
                  >
                    {c.name}
                  </Link>
                ))}
              </div>
            )}
          </div>
          <Link
            to="/search"
            className={`whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors ${
              location.pathname.startsWith('/search')
                ? 'text-primary-600'
                : 'text-foreground-700 hover:text-primary-600'
            }`}
          >
            搜索
          </Link>
          <Link
            to="/about"
            className={`whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors ${
              location.pathname.startsWith('/about')
                ? 'text-primary-600'
                : 'text-foreground-700 hover:text-primary-600'
            }`}
          >
            关于我们
          </Link>
        </nav>

        <form onSubmit={submitSearch} className="ml-auto hidden md:flex">
          <div className="flex h-10 items-center gap-2 rounded-full border border-background-200 bg-background-100 pl-3 pr-1">
            <i className="ri-search-line text-base text-foreground-500" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="搜索剧名或剧情关键词"
              className="h-full w-40 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none xl:w-52"
            />
            <button
              type="submit"
              className="flex h-8 items-center whitespace-nowrap rounded-full bg-primary-500 px-4 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              搜索
            </button>
          </div>
        </form>

        <button
          type="button"
          aria-label="打开菜单"
          onClick={() => setMobileOpen((v) => !v)}
          className="ml-auto flex h-10 w-10 items-center justify-center rounded-md border border-background-200 text-foreground-800 transition-colors hover:bg-background-100 lg:hidden"
        >
          <i className={`${mobileOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl`} />
        </button>
      </div>

      {/* mobile panel */}
      {mobileOpen && (
        <div className="animate-fade-in border-t border-background-200 bg-background-50 px-4 pb-5 pt-4 lg:hidden">
          <form onSubmit={submitSearch} className="mb-4 flex h-11 items-center gap-2 rounded-full border border-background-200 bg-background-100 pl-3 pr-1">
            <i className="ri-search-line text-base text-foreground-500" />
            <input
              ref={mobileSearchRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="搜索剧名或剧情关键词"
              className="h-full flex-1 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
            />
            <button
              type="submit"
              className="flex h-9 items-center whitespace-nowrap rounded-full bg-primary-500 px-4 text-sm font-medium text-background-50"
            >
              搜索
            </button>
          </form>
          <div className="grid grid-cols-2 gap-2">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className="rounded-lg border border-background-200 px-4 py-3 text-sm font-medium text-foreground-800"
              >
                {item.label}
              </Link>
            ))}
          </div>
          <p className="mb-2 mt-4 text-xs font-semibold uppercase tracking-wide text-foreground-400">剧集分类</p>
          <div className="flex flex-wrap gap-2">
            {categories.map((c) => (
              <Link
                key={c.slug}
                to={`/category/${c.slug}`}
                className="rounded-full bg-secondary-100 px-3 py-1.5 text-sm text-secondary-900"
              >
                {c.name}
              </Link>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}